import { BlockStack, Button, Checkbox, InlineError, LegacyStack, Tag, Text } from '@shopify/polaris'
import React, { useCallback } from 'react'

import { gidToId, useDialogActivator } from '../common/helpers'
import { HelpLabel, LegacyHelpIcon } from '../common/HelpIcon'
import { useShopCurrency } from '../common/Price'
import { ProductQuantityField } from '../common/ProductQuantityField'
import { ProductVariantModal, ProductVariantResult } from '../common/ProductVariantModal'
import {
  normalizeWorkflowActionPrice,
  WorkflowAction,
  workflowActionLabels,
  WorkflowActionPriceType,
  WorkflowProductDataType,
  WorkflowSwapProductDataType,
} from '../common/workflow-models'
import { WorkflowActionPriceFields } from './WorkflowActionPriceFields'

export const SwapProductAction = ({
  params,
  onChange,
  index,
  sourceProductError,
  targetProductError,
  currentPriceError,
  quantityError,
}: {
  params: WorkflowSwapProductDataType
  onChange: (params: WorkflowAction, index: number) => any
  index: number
  sourceProductError: string | null
  targetProductError: string | null
  currentPriceError: string | null
  quantityError: string | null
}) => {
  const shopCurrency = useShopCurrency()
  const sourceProductModalActivator = useDialogActivator()
  const targetProductModalActivator = useDialogActivator()

  const [sourceProductData, targetProductData, quantity, currentPrice] = [
    (params[0] ?? []) as WorkflowProductDataType,
    (params[1] ?? []) as WorkflowProductDataType,
    params[2] ?? null,
    (params[3] ?? null) as WorkflowActionPriceType,
  ]

  const handleUpdate = useCallback(
    (params: WorkflowSwapProductDataType) => {
      onChange(['swap-product', params], index)
    },
    [index, onChange]
  )

  const perCurrencyPrices =
    currentPrice === null ? null : normalizeWorkflowActionPrice(currentPrice, shopCurrency)

  return (
    <>
      <LegacyStack vertical>
        <Text variant="headingSm" as="h2">
          {workflowActionLabels['swap-product']}
          <LegacyHelpIcon>
            The selected product variant to swap from will be replaced with the new product variant.
          </LegacyHelpIcon>
        </Text>

        <LegacyStack>
          <Text as="p">Product variant to swap from:</Text>
          {sourceProductData.length === 0 && (
            <Text as="span" tone="subdued">
              No variant selected
            </Text>
          )}
          {sourceProductData.length > 0 && (
            <Tag key={sourceProductData[0]}>{sourceProductData[1]}</Tag>
          )}
        </LegacyStack>

        <Button onClick={sourceProductModalActivator.show} size="slim">
          Select product variant
        </Button>
        {sourceProductError && <InlineError message={sourceProductError} fieldID="" />}

        <LegacyStack>
          <Text as="p">Product variant to swap to:</Text>
          {targetProductData.length > 0 && (
            <Tag key={targetProductData[0]}>{targetProductData[1]}</Tag>
          )}
          {targetProductData.length === 0 && (
            <Text as="span" tone="subdued">
              No variant selected
            </Text>
          )}
        </LegacyStack>
        <Button onClick={targetProductModalActivator.show} size="slim">
          Select product variant
        </Button>
        {targetProductError && <InlineError message={targetProductError} fieldID="" />}

        <BlockStack gap="200">
          <Checkbox
            checked={perCurrencyPrices !== null}
            onChange={(checked) =>
              handleUpdate([
                sourceProductData,
                targetProductData,
                quantity,
                checked ? { [shopCurrency]: '0.00' } : null,
              ])
            }
            label={
              <HelpLabel tooltip='Set the price per currency. Subscriptions are billed in their own currency, so this action applies only when a price for the subscription&apos;s currency is listed. Choose "Any currency" to use one price for every currency that is not listed. For pre-paid subscriptions, you need to manually multiply it by the amount of pre-paid deliveries.'>
                Change unit price
              </HelpLabel>
            }
          />
          {perCurrencyPrices !== null && (
            <WorkflowActionPriceFields
              prices={perCurrencyPrices}
              onChange={(prices) =>
                handleUpdate([sourceProductData, targetProductData, quantity, prices])
              }
              error={currentPriceError}
              index={index}
              bannerText="This product will only be swapped for subscriptions billed in the currencies listed here."
            />
          )}
        </BlockStack>

        <ProductQuantityField
          label={
            <Checkbox
              checked={quantity !== null}
              onChange={(checked) =>
                handleUpdate([
                  sourceProductData,
                  targetProductData,
                  checked ? 1 : null,
                  currentPrice,
                ])
              }
              label="Change quantity"
            />
          }
          type="number"
          value={quantity ?? 1}
          min="1"
          onChange={(val: number) =>
            handleUpdate([sourceProductData, targetProductData, val, currentPrice])
          }
          disabled={quantity === null}
          error={quantityError ?? undefined}
        />
      </LegacyStack>
      <ProductVariantModal
        currency={shopCurrency}
        open={sourceProductModalActivator.open}
        onClose={sourceProductModalActivator.close}
        onSelect={(selected: ProductVariantResult) => {
          handleUpdate([
            [gidToId(selected.id), selected.title],
            targetProductData,
            quantity,
            currentPrice,
          ])
          sourceProductModalActivator.close()
        }}
      />
      <ProductVariantModal
        currency={shopCurrency}
        open={targetProductModalActivator.open}
        onClose={targetProductModalActivator.close}
        onSelect={(selected: ProductVariantResult) => {
          handleUpdate([
            sourceProductData,
            [gidToId(selected.id), selected.title],
            quantity,
            currentPrice,
          ])
          targetProductModalActivator.close()
        }}
      />
    </>
  )
}
